import { type ShippingAccountView, formatShippingAccountLine } from './checkout-domain'

export interface ShippingAccountPickerProps {
  /**
   * The tenant's saved shipping accounts (own-account carrier book, UID-206). Rendered as a
   * radio list; the chosen `selectedShippingAccountId` is what the caller sends as the
   * `shippingAccountId` reference (resolved server-side). `overseasOnly` shows a badge but does
   * NOT gate selection (product decision). Empty list → only the "+ Add" affordance shows.
   */
  readonly shippingAccounts: readonly ShippingAccountView[]
  readonly selectedShippingAccountId: string | null
  readonly onSelectShippingAccount: (id: string) => void
  readonly onAddShippingAccount: () => void
  readonly busy?: boolean
  /**
   * The radio-group `name`. Distinct per host so two pickers never collide if both ever mount
   * (checkout §2 vs RFQ compose). Defaults to the checkout value for a behavior-preserving
   * extraction of the original inline list.
   */
  readonly name?: string
  /** Copy shown when the book is empty. Host-specific (checkout vs RFQ compose). */
  readonly emptyLabel?: string
}

/**
 * UID-201 (ADR-0076) — the saved SHIPPING ACCOUNT radio list (prototype `co.shipAccts`,
 * `Fictiv Platform.dc.html` L2018-2039). EXTRACTED verbatim from `ShippingMethodStep`'s inline
 * own-account list so checkout §2 and RFQ compose share ONE control over ONE canonical book
 * (ADR-0067 §7 — no divergent duplicate concepts). Behavior-preserving: the markup, classes,
 * roles, and copy match the original inline list; only the radio-group `name` and the empty-state
 * copy are parameterized so the two hosts don't collide.
 *
 * Client-safe by construction: it renders only the masked `ShippingAccountView` (`maskedAccount`
 * last-4) — the full carrier account number never reaches this component (it lives server-side and
 * is resolved onto the RFQ/order snapshot at write time).
 */
export function ShippingAccountPicker({
  shippingAccounts,
  selectedShippingAccountId,
  onSelectShippingAccount,
  onAddShippingAccount,
  busy = false,
  name = 'shipping-account',
  emptyLabel = 'Add a shipping account to bill this order to your carrier.',
}: ShippingAccountPickerProps) {
  return (
    <div className="checkout-shipping-account-list" role="radiogroup" aria-label="Shipping account">
      <p className="checkout-shipping-account-list-header">Shipping account</p>
      {shippingAccounts.length === 0 ? (
        <p className="checkout-shipping-account-empty">{emptyLabel}</p>
      ) : null}
      {shippingAccounts.map((account) => (
        <label key={account.id} className="checkout-shipping-account-row">
          <input
            className="radio-card-input"
            type="radio"
            name={name}
            value={account.id}
            checked={selectedShippingAccountId === account.id}
            disabled={busy}
            onChange={() => onSelectShippingAccount(account.id)}
          />
          <span className="checkout-shipping-account-row-text">
            <span className="checkout-shipping-account-row-name">
              {account.holderName ?? account.carrier}
            </span>
            <span className="checkout-shipping-account-row-detail">
              {formatShippingAccountLine(account)}
            </span>
          </span>
          {account.overseasOnly ? (
            <span className="checkout-shipping-account-badge">Overseas only</span>
          ) : null}
        </label>
      ))}
      <button
        type="button"
        className="checkout-shipping-account-add"
        onClick={onAddShippingAccount}
        disabled={busy}
      >
        <span aria-hidden="true">＋</span> Add new shipping account
      </button>
    </div>
  )
}
